/**
 * Todos los textos de interfaz. Ningún componente escribe copy inline.
 * Español neutro con inflexión rioplatense: voseo en imperativos.
 */
export const es = {
  app: {
    name: 'Relatario',
    tagline: 'Historias que alguien tenía que escribir',
    skipToContent: 'Saltar al contenido',
    loading: 'Cargando…',
    close: 'Cerrar',
    cancel: 'Cancelar',
    save: 'Guardar',
    saving: 'Guardando…',
    saved: 'Guardado',
    retry: 'Reintentar',
    back: 'Volver',
    next: 'Siguiente',
    previous: 'Anterior',
    confirm: 'Confirmar',
    delete: 'Eliminar',
    edit: 'Editar',
    more: 'Ver más',
    less: 'Ver menos',
    optional: 'opcional',
    required: 'obligatorio',
    of: 'de',
    search: 'Buscar',
  },

  contentType: {
    fanfic: 'Fanfic',
    original: 'Original',
    fanficPlural: 'Fanfics',
    originalPlural: 'Historias originales',
    all: 'Todo',
    toggleLabel: 'Tipo de contenido',
    switchedTo: (type: string) => `Estás viendo ${type}`,
    fanficDescription: 'Obras derivadas de universos ajenos',
    originalDescription: 'Universos propios, personajes propios',
  },

  nav: {
    home: 'Inicio',
    search: 'Buscar',
    library: 'Biblioteca',
    write: 'Escribir',
    profile: 'Perfil',
    notifications: 'Notificaciones',
    admin: 'Moderación',
    menu: 'Menú',
    signIn: 'Entrar',
    signOut: 'Cerrar sesión',
    settings: 'Ajustes',
  },

  theme: {
    toggle: 'Cambiar tema',
    light: 'Claro',
    dark: 'Oscuro',
    system: 'Según el sistema',
    sepia: 'Sepia',
  },

  auth: {
    signInTitle: 'Entrá a Relatario',
    signInSubtitle: 'Para seguir historias, puntuar y publicar lo tuyo.',
    withGoogle: 'Continuar con Google',
    orEmail: 'o con tu correo',
    email: 'Correo electrónico',
    password: 'Contraseña',
    passwordHint: 'Mínimo 8 caracteres.',
    signIn: 'Entrar',
    signUp: 'Crear cuenta',
    noAccount: '¿Todavía no tenés cuenta?',
    hasAccount: '¿Ya tenés cuenta?',
    forgotPassword: '¿Olvidaste la contraseña?',
    resetSent: 'Te mandamos un correo para restablecerla.',
    signingIn: 'Entrando…',
    errors: {
      invalidCredentials: 'El correo o la contraseña no coinciden.',
      emailInUse: 'Ese correo ya tiene una cuenta.',
      weakPassword: 'La contraseña es demasiado corta.',
      popupClosed: 'Cerraste la ventana de Google antes de terminar.',
      network: 'No pudimos conectarnos. Revisá tu conexión y probá de nuevo.',
      generic: 'Algo falló al entrar. Probá de nuevo en un momento.',
      tooManyRequests: 'Demasiados intentos seguidos. Esperá unos minutos.',
    },
  },

  onboarding: {
    title: 'Elegí cómo te van a leer',
    subtitle: 'Estos datos aparecen en tus historias y en tu perfil público.',
    displayName: 'Nombre visible',
    displayNamePlaceholder: 'Cómo querés que te llamen',
    handle: 'Nombre de usuario',
    handleHelp: 'Sólo letras minúsculas, números y guiones bajos. No se puede cambiar después.',
    handleChecking: 'Verificando disponibilidad…',
    handleAvailable: 'Está libre.',
    handleTaken: 'Ese nombre de usuario ya está en uso.',
    handleInvalid: 'Entre 3 y 20 caracteres: letras minúsculas, números y guion bajo.',
    submit: 'Crear mi perfil',
    creating: 'Creando tu perfil…',
    error: 'No pudimos crear el perfil. No es por el nombre: falló la conexión con el servidor.',
    errorSession: 'Se cerró tu sesión mientras completabas el perfil. Entrá de nuevo.',
  },

  /** Portada pública. Única superficie donde el diseño es el producto. */
  landing: {
    headline: 'Nadie va a leer tu primer capítulo.',
    headlineTurn: 'Publicalo igual.',
    subhead:
      'Relatario es donde se publican fanfics y obra original en español. Los dos en serio: ninguno es el pariente pobre del otro.',
    ctaFanfic: 'Publicar un fanfic',
    ctaOriginal: 'Publicar algo propio',
    ctaRead: 'Sólo quiero leer',
    fanficPitch: 'Obra derivada',
    fanficBody:
      'Tu fandom sale de un catálogo, no de un campo de texto. Ships, advertencias y el aviso de obra sin fines de lucro que te cubre las espaldas.',
    originalPitch: 'Obra propia',
    originalBody:
      'Universo tuyo, personajes tuyos. Géneros, público objetivo, y un lugar donde no competís contra diez mil fanfics por la misma pantalla.',

    fanficFields: ['Fandom del catálogo', 'Ships', 'Tipo de relación', 'Advertencias'],
    originalFields: ['Géneros', 'Público objetivo', 'Sinopsis', 'Estado de la obra'],

    stampNew: 'Ficha 001 · sin publicar',
    drawerFandoms: 'Cajón · fandoms',
    drawerReading: 'Cajón · leyéndose ahora',
    claimsTitle: 'Cuatro cosas que vas a notar enseguida',

    fandomFieldTitle: 'Los fandoms salen de acá, no de un campo de texto',
    browseCatalog: 'Mirá si está el tuyo, y si no, pedilo',
    fandomFieldBody:
      'Un catálogo controlado significa que buscar tu fandom te devuelve todo lo que se escribió, y no cuatro formas distintas de escribir el mismo nombre.',

    claims: [
      {
        title: 'Tu fanfic no es contenido de segunda',
        body: 'Misma estructura, mismas herramientas y el mismo lugar en el buscador que la obra original. Lo único que cambia es la metadata, porque las audiencias son distintas.',
      },
      {
        title: 'Se te puede escuchar, no sólo leer',
        body: 'Grabás hasta tres minutos presentando tu historia y quedan arriba de la ficha, en tu voz: de qué va, por qué la escribís, o gracias a quien te lee. Va con transcripción, así también llega a quien no puede escucharla.',
      },
      {
        title: 'El lector es la pantalla que más trabajamos',
        body: 'Tres temas, letra de 15 a 26 píxeles, se acuerda de dónde ibas y cambia de capítulo con las flechas. Si estás leyendo, el resto de la interfaz se corre.',
      },
      {
        title: 'No hay ranking de personas',
        body: 'Se puntúan historias con promedio ponderado, así una sola estrella de cinco no le gana a doscientos votos. No hay rachas, ni insignias, ni nada que convierta escribir en una competencia.',
      },
    ],

    closingTitle: 'El primer capítulo es el más difícil.',
    closingBody: 'Después es sólo escribir.',
    closingCta: 'Empezar a publicar',
    footerNote: 'Hecho en Argentina. Los fanfics son obra derivada sin fines de lucro.',
  },

  home: {
    trending: 'Tendencias',
    trendingHint: 'Lo que más se está leyendo ahora',
    topRated: 'Mejor puntuadas',
    topRatedHint: 'Ordenadas por promedio ponderado, no por promedio simple',
    recent: 'Recientes',
    recentHint: 'Lo último que se publicó',
    popularFandoms: 'Fandoms populares',
    seeAll: 'Ver todo',
  },

  story: {
    chapters: 'Capítulos',
    chapter: 'Capítulo',
    chapterCount: (n: number) => (n === 1 ? '1 capítulo' : `${n} capítulos`),
    words: 'palabras',
    wordCount: (n: number) => `${n.toLocaleString('es-AR')} palabras`,
    views: 'lecturas',
    rating: 'Puntuación',
    ratingCount: (n: number) => (n === 1 ? '1 voto' : `${n} votos`),
    noRatingsYet: 'Sin votos todavía',
    comments: 'Comentarios',
    followers: 'Seguidores',
    by: 'por',
    synopsis: 'Sinopsis',
    read: 'Leer',
    readFirst: 'Empezar a leer',
    continueReading: 'Seguir leyendo',
    follow: 'Seguir',
    following: 'Siguiendo',
    unfollow: 'Dejar de seguir',
    rate: 'Puntuar',
    rateAria: (n: number) => `Puntuar con ${n} de 5`,
    yourRating: 'Tu puntuación',
    removeRating: 'Quitar mi puntuación',
    share: 'Compartir',
    report: 'Denunciar',
    updatedAt: 'Actualizada',
    publishedAt: 'Publicada',
    language: 'Idioma',
    warnings: 'Advertencias de contenido',
    noWarnings: 'Sin advertencias declaradas',
    fandom: 'Fandom',
    ships: 'Ships',
    relationship: 'Relación',
    genres: 'Géneros',
    audience: 'Público',
    status: {
      draft: 'Borrador',
      ongoing: 'En curso',
      completed: 'Completa',
      hiatus: 'En pausa',
    },
    fanficDisclaimer:
      'Obra derivada sin fines de lucro. Los personajes y el universo pertenecen a sus autores originales; este relato no está autorizado por ellos ni genera ingresos.',
  },

  voice: {
    heading: 'La voz de quien escribe',
    byAuthor: (name: string) => `${name} te presenta esta historia`,
    play: 'Escuchar la presentación',
    pause: 'Pausar',
    loading: 'Cargando el audio…',
    playFailed: 'No se pudo reproducir el audio.',
    report: 'Denunciar el audio',

    // Editor
    editorHeading: 'Presentación en voz',
    editorHelp:
      'Grabá hasta tres minutos presentándote. Contá de qué va la historia, por qué la escribís o agradecele a quien te lee. No hace falta que leas el capítulo: lo que suma es tu voz.',
    unsupported:
      'Este navegador no puede grabar audio. Probá desde Chrome, Edge, Firefox o Safari.',
    record: 'Grabar',
    recording: 'Grabando',
    stop: 'Terminar',
    listen: 'Escuchar',
    rerecord: 'Grabar de nuevo',
    use: 'Usar esta grabación',
    discard: 'Descartar',
    remove: 'Quitar la presentación',
    level: 'Nivel de entrada',
    silent: 'No entra sonido. Revisá el micrófono.',
    limitReached: 'Llegaste a los tres minutos y la grabación se cortó sola.',
    permissionDenied:
      'El navegador bloqueó el micrófono. Habilitalo desde el candado de la barra de direcciones.',
    // Muchos auriculares conectados por la pantalla o por un monitor sólo dan
    // salida de audio. La computadora no siempre tiene entrada; el teléfono
    // siempre la tiene, y es desde donde va a grabar la mayoría.
    micNotFound: 'No encontramos ningún micrófono conectado. Probá desde el celular.',
    micBusy:
      'El micrófono lo está usando otra aplicación. Cerrala y volvé a intentar.',
    recordFailed: 'No se pudo iniciar la grabación.',
    uploadFailed: 'No se pudo subir la grabación.',
    saveFailed: 'Se subió el audio pero no se pudo guardar en la historia.',
    tooHeavy: 'La grabación quedó demasiado pesada. Probá con una más corta.',
    tip: 'Grabá en un lugar sin eco y hablá cerca del micrófono.',

    transcript: '¿Qué decís en el audio?',
    transcriptHelp:
      'Escribilo acá para que también lo lea quien no puede escucharlo, y para que aparezca en los buscadores. No hace falta que sea palabra por palabra.',
    transcriptListening: 'Lo escribimos solos mientras grabás…',
    transcriptAuto:
      'Lo transcribió el navegador mientras hablabas, así que puede tener errores: revisalo antes de guardar. Sirve para que también lo lea quien no puede escucharlo, y para que aparezca en los buscadores.',
    transcriptRead: 'Leer lo que dice',
    transcriptMissing: 'Esta presentación no tiene texto.',
  },

  reader: {
    settings: 'Ajustes de lectura',
    fontSize: 'Tamaño de letra',
    increase: 'Agrandar la letra',
    decrease: 'Achicar la letra',
    theme: 'Tema de lectura',
    progress: 'Progreso del capítulo',
    nextChapter: 'Capítulo siguiente',
    prevChapter: 'Capítulo anterior',
    backToStory: 'Volver a la historia',
    chapterList: 'Índice de capítulos',
    authorNoteBefore: 'Nota de la autoría',
    authorNoteAfter: 'Nota final',
    endOfChapter: 'Fin del capítulo',
    lastChapter: 'Es el último capítulo publicado.',
    rateThisStory: '¿Qué te pareció?',
    resumeFound: 'Retomamos donde lo dejaste.',
    keyboardHint: 'Usá ← y → para cambiar de capítulo.',
  },

  editor: {
    newStory: 'Nueva historia',
    step: (n: number, total: number) => `Paso ${n} de ${total}`,
    chooseType: '¿Qué vas a publicar?',
    chooseTypeHint: 'Esta elección define el resto del formulario y no se puede cambiar después.',
    title: 'Título',
    titlePlaceholder: 'El título de tu historia',
    synopsis: 'Sinopsis',
    synopsisPlaceholder: 'De qué va, en pocas líneas.',
    cover: 'Portada',
    coverHint: 'Se recorta a 2:3. Máximo 2 MB.',
    coverChoose: 'Elegir imagen',
    coverChange: 'Cambiar portada',
    coverRemove: 'Quitar portada',
    coverCropTitle: 'Recortá la portada',
    coverSource: '¿De dónde salió esta imagen?',
    coverSourceOwn: 'La hice yo',
    coverSourcePermission: 'La hizo otra persona y me dio permiso',
    coverSourceFree: 'Es de dominio público o licencia libre',
    coverSourceHelp:
      'Una imagen sacada de internet suele ser el dibujo de alguien que no dio permiso. Si no entrás en ninguna de las tres, dejá la historia sin portada: el sitio le arma una con el título.',
    coverSourceRequired: 'Elegí de dónde salió la imagen.',
    coverUploadFailed: 'No se pudo subir la imagen.',

    /* Las tres formas de conseguir una portada. */
    coverWaysTitle: 'Cómo conseguir la portada',
    coverWayGenerate: 'Generar una',
    coverWayArt: 'Buscar arte libre',
    coverWayUpload: 'Subir una imagen',

    coverGenTitle: 'Generar portada',
    coverGenIntro:
      'Se arma con el título de tu historia. No usa ninguna imagen, así que no hay derechos de nadie de por medio.',
    coverPalettes: 'Paletas para empezar',
    coverColors: 'Colores',
    coverPartBg: 'Fondo',
    coverPartInk: 'Texto',
    coverPartAccent: 'Adorno',
    coverDecoration: 'Adorno',
    coverTitleBlock: 'Título',
    coverAlignLeft: 'A la izquierda',
    coverAlignCenter: 'Centrado',
    coverTitleSize: 'Tamaño',
    coverReset: 'Reiniciar',
    coverContrastOk: (ratio: string) => `Contraste ${ratio}:1. Se lee bien.`,
    coverContrastLow: (ratio: string) =>
      `Contraste ${ratio}:1. Por debajo de 4,5 el título casi no se lee en la grilla.`,
    coverGenConfirm: 'Usar esta portada',
    coverGenWorking: 'Generando…',

    coverArtTitle: 'Arte de dominio público',
    coverArtIntro:
      'Obra de Wikimedia Commons en dominio público o CC0: se puede usar sin pedir permiso ni acreditar a nadie.',
    coverArtSearch: 'Buscar por tema, autor o época',
    coverArtPlaceholder: 'Hokusai, Van Gogh, tormenta…',
    coverArtThemes: 'O probá con un tema',
    coverArtLangNote:
      'Podés buscar en español o en inglés. En inglés suele traer más, porque es el idioma en que están fichadas la mayoría de las obras.',
    coverArtFailedSource: 'No se pudo consultar Wikimedia. Puede ser algo pasajero.',
    coverArtSearching: 'Buscando…',
    coverArtEmpty: 'No encontramos nada con esa búsqueda. Probá con otra palabra.',
    coverArtLoading: 'Trayendo la obra…',
    coverArtHint: 'Elegí una obra y después la recortás.',

    coverTextTitle: 'El título encima',
    coverTextIntro:
      'Con el título puesto se lee como una tapa de libro y no como una imagen suelta. El velo se ajusta solo para que las letras se lean sobre esta imagen.',
    coverTextShow: 'Poner el título sobre la imagen',
    coverTextDragHint: 'Arrastrá el título donde quieras. También lo movés con las flechas.',
    coverTextDragAria: 'Vista previa de la portada. Arrastrá o usá las flechas para mover el título.',
    coverTone: 'Color del texto',
    coverToneAuto: 'Automático',
    coverToneCustom: 'Otro',
    coverToneHelp:
      'Automático elige el que menos tapa la imagen. Con cualquier otro color, el velo se ajusta solo hasta que el título se lea.',
    coverBinding: 'Encuadernado',
    coverSaveFailed: 'La imagen subió, pero no se pudo guardar en la historia.',
    coverZoom: 'Acercar',
    coverConfirm: 'Usar este recorte',
    coverCompressing: 'Comprimiendo…',
    coverUploading: 'Subiendo…',
    fandom: 'Fandom',
    fandomPlaceholder: 'Buscá el fandom por nombre',
    fandomHelp: 'Sale de un catálogo. Si no está, avisanos y lo agregamos.',
    fandomNoResults: 'No encontramos ese fandom en el catálogo.',
    fandomChangeConfirm:
      '¿Cambiar el fandom? La historia se muda de estante y deja de aparecer en el anterior.',
    fandomRequestCta: (name: string) => `Pedir que agreguemos «${name}»`,
    fandomRequestTitle: 'Pedir un fandom',
    fandomRequestHelp:
      'El catálogo es cerrado para que no existan cuatro versiones del mismo fandom. Si el tuyo falta, pedilo: lo revisamos y te avisamos.',
    fandomRequestName: 'Nombre del fandom',
    fandomRequestNote: 'Contanos de qué es',
    fandomRequestNotePlaceholder: 'Libro, serie, videojuego… y cómo se lo suele nombrar.',
    fandomRequestSubmit: 'Enviar pedido',
    fandomRequestFailed: 'No se pudo enviar el pedido.',
    fandomRequestSignIn: 'Entrá para pedir que lo agreguemos.',
    fandomRequestSent: 'Pedido enviado. Si lo aprobamos, vas a poder elegirlo al publicar.',
    ships: 'Ships',
    shipsPlaceholder: 'Personaje A/Personaje B',
    shipsHelp: 'Hasta 5. Enter para agregar.',
    relationshipType: 'Tipo de relación',
    warnings: 'Advertencias de contenido',
    warningsHelpFanfic: 'Obligatorias. Si no aplica ninguna, marcá "Sin advertencias".',
    warningsHelpOriginal: 'Opcionales, pero ayudan a que te lean quienes quieren leerte.',
    genres: 'Géneros',
    genresHelp: 'Hasta 3.',
    audience: 'Público objetivo',
    language: 'Idioma',
    status: 'Estado',
    create: 'Crear historia',
    creating: 'Creando…',
    chapterTitle: 'Título del capítulo',
    chapterTitlePlaceholder: 'Capítulo sin título',
    chapterBody: 'Texto del capítulo',
    chapterBodyPlaceholder: 'Empezá a escribir…',
    notesBefore: 'Nota antes del capítulo',
    notesAfter: 'Nota después del capítulo',
    autosaveIdle: 'Borrador al día',
    autosavePending: 'Cambios sin guardar',
    autosaveSaving: 'Guardando…',
    autosaveError: 'No pudimos guardar. Reintentando…',
    wordsWritten: (n: number) => `${n.toLocaleString('es-AR')} palabras`,
    publish: 'Publicar capítulo',
    unpublish: 'Pasar a borrador',
    published: 'Publicado',
    publishedConfirm: 'Listo, tu capítulo ya se puede leer.',
    newChapter: 'Nuevo capítulo',
    reorder: 'Reordenar',
    reorderDone: 'Listo',
    moveUp: 'Subir',
    moveDown: 'Bajar',
    deleteChapter: '¿Eliminar este capítulo?',
    deleteChapterHint: 'No se puede deshacer.',
    imageUploadFailed: 'No se pudo subir la imagen.',
    toolbar: {
      bold: 'Negrita',
      italic: 'Cursiva',
      heading2: 'Título',
      heading3: 'Subtítulo',
      quote: 'Cita',
      divider: 'Separador',
      bulletList: 'Lista',
      orderedList: 'Lista numerada',
      image: 'Insertar imagen',
      undo: 'Deshacer',
      redo: 'Rehacer',
    },
    fanficNotice: 'Vas a publicar una obra derivada',
    fanficNoticeBody:
      'Los fanfics en Relatario son sin fines de lucro y no son monetizables. Declarar el fandom y las advertencias de contenido es obligatorio.',
  },

  dashboard: {
    title: 'Tus historias',
    subtitle: 'Todo lo que escribiste, por tipo.',
    drafts: 'Borradores',
    published: 'Publicadas',
    stats: 'Estadísticas',
    totalViews: 'Lecturas',
    totalRatings: 'Votos',
    avgRating: 'Promedio',
    totalFollowers: 'Seguidores',
    newStory: 'Escribir algo nuevo',
    pendingDrafts: (n: number) =>
      n === 1 ? 'Tenés 1 borrador sin publicar' : `Tenés ${n} borradores sin publicar`,
  },

  filters: {
    title: 'Filtros',
    apply: 'Aplicar',
    clear: 'Limpiar',
    activeCount: (n: number) => (n === 1 ? '1 filtro' : `${n} filtros`),
    status: 'Estado',
    language: 'Idioma',
    minChapters: 'Mínimo de capítulos',
    minRating: 'Puntuación mínima',
    excludeWarnings: 'Excluir advertencias',
    fandom: 'Fandom',
    relationshipType: 'Tipo de relación',
    genres: 'Géneros',
    audience: 'Público',
    sort: 'Ordenar por',
    sortTrending: 'Tendencias',
    sortRating: 'Mejor puntuadas',
    sortRecent: 'Más recientes',
    sortUpdated: 'Actualizadas hace poco',
  },

  search: {
    lookingForFandom: '¿Buscás un fandom?',
    authorFound: 'Autor',
    placeholder: 'Buscar historias, fandoms, autores',
    label: 'Buscar en Relatario',
    submit: 'Buscar',
    resultsFor: (q: string) => `Resultados para “${q}”`,
    tabAll: 'Todo',
    tabFanfic: 'Fanfic',
    tabOriginal: 'Original',
    typeToStart: 'Escribí algo para empezar a buscar.',
    minLength: 'Escribí al menos 2 caracteres.',
  },

  comments: {
    title: 'Comentarios',
    placeholder: 'Dejá tu comentario',
    submit: 'Comentar',
    submitting: 'Publicando…',
    reply: 'Responder',
    replyPlaceholder: 'Escribí tu respuesta',
    replies: (n: number) => (n === 1 ? '1 respuesta' : `${n} respuestas`),
    showReplies: 'Ver respuestas',
    hideReplies: 'Ocultar respuestas',
    like: 'Me gusta',
    liked: 'Ya no me gusta',
    authorBadge: 'Autoría',
    edited: 'editado',
    loadMore: 'Cargar más comentarios',
    signInToComment: 'Entrá para comentar.',
    tooLong: 'El comentario no puede pasar los 2000 caracteres.',
    hidden: 'Comentario oculto por moderación.',
    deleteConfirm: '¿Borrar tu comentario?',
  },

  art: {
    title: 'Fan art',
    subtitle: 'Dibujos de la comunidad. El fandom no es sólo leer y escribir.',
    subject: 'las obras',
    upload: 'Subir un dibujo',
    uploadTitle: 'Subir un dibujo',
    file: 'Imagen',
    fileHint: 'JPG, PNG o WebP. Se guarda en dos tamaños automáticamente.',
    fileChoose: 'Elegir imagen',
    fileChange: 'Cambiar imagen',
    artTitle: 'Título',
    artTitlePlaceholder: '¿Qué dibujaste?',
    description: 'Descripción',
    descriptionPlaceholder: 'Contá algo del dibujo, si querés.',
    authorship: 'Autoría',
    authorshipOwn: 'Lo dibujé yo',
    authorshipCredited: 'Lo dibujó otra persona con mi encargo o permiso',
    authorshipWhy:
      'El robo de arte es el problema más común en las comunidades de fandom, y acreditar no alcanza: subir el dibujo de alguien sin su permiso sigue estando mal aunque pongas su nombre. Si te lo hicieron por encargo o te lo regalaron, subilo acreditando y enlazando la publicación original.',
    artistName: 'Nombre de quien lo dibujó',
    artistUrl: 'Enlace a la publicación original',
    artistUrlPlaceholder: 'https://…',
    permissionConfirm: 'Confirmo que quien lo dibujó me autorizó a publicarlo acá',
    permissionConfirmHelp:
      'Si no tenés permiso, enlazá el dibujo en un comentario en vez de subirlo. Es lo que prefieren los dibujantes.',
    linkedStory: 'Historia que ilustra',
    linkedStoryNone: 'Ninguna',
    warningsHelp: 'Obligatorias. Si no aplica ninguna, marcá «Sin advertencias».',
    publish: 'Publicar dibujo',
    publishing: 'Subiendo…',
    publishedConfirm: 'Listo, tu dibujo ya está en la galería.',

    by: 'Subido por',
    drawnBy: 'Dibujado por',
    viewOriginal: 'Ver publicación original',
    like: 'Me gusta',
    liked: 'Ya no me gusta',
    save: 'Guardar',
    saved: 'Guardado',
    saveHint: 'Guardar lo agrega a tu biblioteca. Nadie ve qué guardaste.',
    download: 'Descargar',
    illustrates: 'Ilustra',
    savedTab: 'Guardados',

    empty: {
      title: 'Todavía no hay dibujos',
      body: 'La galería está vacía. El primer dibujo suele traer los demás.',
      action: 'Subir el primero',
    },
    emptySaved: {
      title: 'No guardaste ningún dibujo',
      body: 'Lo que guardes va a estar acá, y sólo lo ves vos.',
      action: 'Ir a la galería',
    },
    notFound: {
      title: 'Este dibujo no existe',
      body: 'Puede que lo hayan borrado o que el link esté mal.',
      action: 'Volver a la galería',
    },
    validation: {
      fileRequired: 'Elegí una imagen.',
      artistRequired: 'Si el dibujo no es tuyo, acreditá a quien lo hizo.',
      permissionRequired: 'Sin el permiso de quien lo dibujó no lo podemos publicar.',
      urlRequired: 'Poné el enlace a la publicación original.',
      urlInvalid: 'Tiene que ser un enlace que empiece con http:// o https://',
      warningsRequired: 'Marcá al menos una advertencia de contenido.',
    },
  },

  board: {
    title: 'Tablón',
    subtitle: 'Donde se habla de lo que se escribe.',
    /** Cómo se nombra lo que trae esta pantalla, para los mensajes de error. */
    subject: 'los temas',
    newThread: 'Abrir un tema',
    newThreadTitle: 'Abrir un tema nuevo',
    threadTitle: 'Título',
    threadTitlePlaceholder: '¿De qué querés hablar?',
    threadBody: 'Tu mensaje',
    threadBodyPlaceholder: 'Contá el tema. Cuanto más concreto, más fácil que alguien responda.',
    category: 'Categoría',
    publish: 'Publicar tema',
    publishing: 'Publicando…',
    reply: 'Responder',
    replyPlaceholder: 'Escribí tu respuesta',
    replySubmit: 'Responder',
    replyCount: (n: number) => (n === 1 ? '1 respuesta' : `${n} respuestas`),
    noReplies: 'Todavía nadie respondió.',
    lastActivity: 'Última actividad',
    pinned: 'Fijado',
    pin: 'Fijar arriba',
    unpin: 'Dejar de fijar',
    pinFailed: 'No se pudo cambiar el fijado.',
    authorBadge: 'Abrió el tema',
    backToBoard: 'Volver al tablón',
    signInToPost: 'Entrá para abrir un tema o responder.',
    categories: {
      general: 'Charla general',
      fandoms: 'Qué fandom falta',
      lecturas: 'Busco quién me lea',
      ayuda: 'Ayuda y dudas',
    },
    categoryHints: {
      general: 'Presentarse, contar en qué andás, discutir cualquier cosa.',
      fandoms:
        'Decí sobre qué querés escribir. Si hay interés, damos de alta el fandom en el catálogo.',
      lecturas: 'Pedí que te lean y comenten. Dejá el link de tu historia.',
      ayuda: 'Dudas sobre cómo funciona Relatario.',
    },
    empty: {
      title: 'El tablón está vacío',
      body: 'Nadie abrió un tema todavía. El primero suele ser el que define de qué se habla acá.',
      action: 'Abrir el primero',
    },
    emptyCategory: {
      title: 'Nada en esta categoría todavía',
      body: 'Podés abrir el primer tema o mirar las otras categorías.',
      action: 'Abrir un tema',
    },
    threadNotFound: {
      title: 'Este tema no existe',
      body: 'Puede que lo hayan borrado o que el link esté mal.',
      action: 'Volver al tablón',
    },
  },

  notifications: {
    title: 'Notificaciones',
    markAllRead: 'Marcar todas como leídas',
    /* Sólo para lectores de pantalla: en pantalla el estado lo dice el punto. */
    unread: 'Sin leer',
    newChapter: (story: string) => `Nuevo capítulo en ${story}`,
    commentReply: 'Respondieron tu comentario',
    storyComment: 'Comentaron tu historia',
    moderation: 'Aviso de moderación',
    /* Selección múltiple para borrar. */
    selectOne: (title: string) => `Seleccionar «${title}»`,
    selectAll: 'Seleccionar todas',
    selectedCount: (n: number) => (n === 1 ? '1 seleccionada' : `${n} seleccionadas`),
    delete: 'Borrar',
    deleteConfirm: (n: number) =>
      n === 1
        ? '¿Borrar esta notificación? No se puede deshacer.'
        : `¿Borrar ${n} notificaciones? No se puede deshacer.`,
  },

  tips: {
    button: (name: string) => `Colaborar con ${name}`,
    /* Va debajo del botón: sin esto parece que el pago lo cobra Relatario. */
    disclaimer:
      'La colaboración va directo a quien escribe, por fuera de Relatario. La plataforma no cobra comisión ni participa del pago.',
  },

  profile: {
    stories: 'Historias',
    followers: 'Seguidores',
    following: 'Siguiendo',
    joined: 'En Relatario desde',
    editProfile: 'Editar perfil',
    bio: 'Sobre vos',
    bioPlaceholder: 'Contá algo, si querés.',
  },

  settings: {
    subtitle: 'Lo que ve el resto cuando te encuentra.',
    viewProfile: 'Ver mi perfil',
    photo: 'Foto de perfil',
    photoHint: 'Se recorta en cuadrado. Máximo 1 MB.',
    photoChoose: 'Elegir foto',
    photoChange: 'Cambiar foto',
    photoRemove: 'Quitar foto',
    photoCropTitle: 'Recortá tu foto',
    photoConfirm: 'Usar esta foto',
    nameLength: 'Entre 2 y 40 caracteres.',
    tipLink: 'Enlace para recibir colaboraciones',
    tipLinkHelp:
      'Opcional. Si lo cargás, aparece un botón en tu perfil para que quien te lee pueda invitarte un café. La plata va directo a vos: Relatario no cobra comisión ni toca el pago.',
    tipLinkSites:
      'Sólo aceptamos Cafecito, Mercado Pago, Ko-fi, Buy Me a Coffee, PayPal.me y Patreon. No pedimos alias ni CBU: son datos de tu cuenta bancaria y no deberían estar publicados en ningún lado.',
    tipLinkInvalid:
      'Tiene que ser un enlace https de alguno de los sitios de la lista, apuntando a tu página.',
    handleFixed: 'Tu nombre de usuario es',
    handleFixedWhy:
      'No se puede cambiar: es parte de la dirección de todas tus historias, y cambiarlo rompería los links que alguien ya compartió.',
    staleAvatarNote:
      'Los comentarios y temas que ya publicaste conservan la foto que tenías al escribirlos.',
  },

  report: {
    title: 'Denunciar contenido',
    reason: 'Motivo',
    description: 'Contanos qué pasó',
    descriptionPlaceholder: 'Cuanto más concreto, más rápido lo resolvemos.',
    submit: 'Enviar denuncia',
    sent: 'Recibimos tu denuncia. La vamos a revisar.',
    reasons: {
      spam: 'Spam o publicidad',
      plagio: 'Plagio',
      'contenido-ilegal': 'Contenido ilegal',
      acoso: 'Acoso o discurso de odio',
      'advertencias-faltantes': 'Faltan advertencias de contenido',
      'portada-inapropiada': 'Portada inapropiada',
      'arte-robado': 'Arte publicado sin permiso',
      'audio-ajeno': 'El audio no es la voz de quien publica',
      otro: 'Otro',
    },
  },

  admin: {
    title: 'Moderación',
    queue: 'Cola de denuncias',
    fandoms: 'Catálogo de fandoms',
    users: 'Usuarios',
    open: 'Abiertas',
    reviewing: 'En revisión',
    resolved: 'Resueltas',
    dismissed: 'Descartadas',
    hideContent: 'Ocultar contenido',
    restoreContent: 'Restaurar contenido',
    suspendUser: 'Suspender usuario',
    suspendDays: 'Días de suspensión',
    resolve: 'Resolver',
    dismiss: 'Descartar',
    resolutionNote: 'Nota de resolución',
    fandomRequests: 'Pedidos',
    approveRequest: 'Aprobar y crear',
    noRequests: {
      title: 'No hay pedidos de fandom',
      body: 'Cuando alguien busque un fandom que no está en el catálogo y lo pida, aparece acá.',
    },
    newFandom: 'Nuevo fandom',
    fandomName: 'Nombre',
    fandomDuplicate: 'Ese fandom ya está en el catálogo. Buscalo en la lista de abajo.',
    fandomAddFailed: 'No se pudo agregar el fandom. Probá de nuevo.',
    fandomAliases: 'Alias (separados por coma)',
    fandomCategory: 'Categoría',
  },

  warnings: {
    violencia: 'Violencia',
    'lenguaje-fuerte': 'Lenguaje fuerte',
    'contenido-sexual': 'Contenido sexual',
    autolesion: 'Autolesión',
    'muerte-de-personaje': 'Muerte de personaje',
    abuso: 'Abuso',
    'consumo-de-sustancias': 'Consumo de sustancias',
    'sin-advertencias': 'Sin advertencias',
  },

  genres: {
    fantasia: 'Fantasía',
    'ciencia-ficcion': 'Ciencia ficción',
    romance: 'Romance',
    terror: 'Terror',
    misterio: 'Misterio',
    thriller: 'Thriller',
    historica: 'Histórica',
    drama: 'Drama',
    aventura: 'Aventura',
    poesia: 'Poesía',
    realismo: 'Realismo',
    humor: 'Humor',
  },

  relationships: {
    gen: 'Gen (sin romance)',
    het: 'Het',
    slash: 'Slash',
    femslash: 'Femslash',
    multi: 'Múltiple',
    otro: 'Otro',
  },

  audiences: {
    infantil: 'Infantil',
    juvenil: 'Juvenil',
    'new-adult': 'New adult',
    adulto: 'Adulto',
  },

  languages: {
    es: 'Español',
    'es-AR': 'Español rioplatense',
    'es-MX': 'Español de México',
    'es-ES': 'Español de España',
    pt: 'Portugués',
    en: 'Inglés',
  },

  fandomCatalog: {
    title: 'Catálogo de fandoms',
    metaDescription:
      'Todos los fandoms sobre los que se puede publicar en Relatario. Si falta el tuyo, pedilo y lo agregamos.',
    subtitle: (n: number) => `${n} fandoms. Si falta el tuyo, pedilo desde acá.`,
    filterLabel: 'Filtrar fandoms',
    filterPlaceholder: 'Escribí para filtrar…',
    noMatch: (term: string) =>
      term ? `No encontramos «${term}» en el catálogo.` : 'No hay fandoms cargados.',
    notWhatYouWanted: '¿No es ninguno de estos?',
    seeAll: 'Ver el catálogo completo',
  },

  fandomCategories: {
    'anime-manga': 'Anime y manga',
    libros: 'Libros',
    cine: 'Cine',
    series: 'Series',
    videojuegos: 'Videojuegos',
    musica: 'Música',
    comics: 'Cómics',
    teatro: 'Teatro',
    otro: 'Otro',
  },

  /** Estados vacíos y de error. Cada uno dice qué pasó y qué hacer. */
  empty: {
    homeFanfic: {
      title: 'Todavía no hay fanfics acá',
      body: 'Nadie publicó nada en este universo por ahora. Podés ser quien empiece.',
      action: 'Publicar un fanfic',
    },
    homeOriginal: {
      title: 'Todavía no hay historias originales',
      body: 'Esta sección se llena con obras propias. Si tenés una guardada, es el momento.',
      action: 'Publicar mi historia',
    },
    searchNoResults: {
      title: (q: string) => `Nada coincide con “${q}”`,
      body: 'Probá con menos palabras, con el nombre del fandom, o revisá la ortografía.',
      action: 'Limpiar la búsqueda',
    },
    filteredNoResults: {
      title: 'Ningún resultado con esos filtros',
      body: 'La combinación es muy estrecha. Sacá alguno y volvé a probar.',
      action: 'Limpiar filtros',
    },
    dashboardNoStories: {
      title: 'Acá van a estar tus historias',
      body: 'Todavía no publicaste nada. El primer capítulo es el más difícil.',
      action: 'Escribir la primera',
    },
    noChapters: {
      title: 'Esta historia no tiene capítulos publicados',
      body: 'La autoría la creó pero todavía no publicó nada.',
      action: 'Volver al inicio',
    },
    noComments: {
      title: 'Sin comentarios todavía',
      body: 'Si llegaste hasta acá, tu comentario es el que va a leer la autoría primero.',
      action: 'Escribir el primero',
    },
    noNotifications: {
      title: 'No tenés notificaciones',
      body: 'Cuando alguien a quien seguís publique un capítulo, te avisamos acá.',
      action: 'Buscar algo para seguir',
    },
    noFollows: {
      title: 'Tu biblioteca está vacía',
      body: 'Seguí historias o autorías y las vas a encontrar todas juntas en este lugar.',
      action: 'Explorar historias',
    },
    fandomNoStories: {
      title: (fandom: string) => `Nadie escribió sobre ${fandom} todavía`,
      body: 'El fandom existe en el catálogo pero está esperando su primera historia.',
      action: 'Escribir la primera',
    },
    adminQueueEmpty: {
      title: 'No hay denuncias pendientes',
      body: 'La cola está vacía. Es la mejor noticia posible.',
      action: 'Ver resueltas',
    },
  },

  errors: {
    loadStories: {
      title: 'No pudimos traer las historias',
      body: 'Puede ser tu conexión o algo de nuestro lado. El pedido no llegó a completarse.',
      action: 'Reintentar',
    },
    loadStory: {
      title: 'No pudimos abrir esta historia',
      body: 'Puede que la hayan despublicado o que el link esté mal.',
      action: 'Volver al inicio',
    },
    loadChapter: {
      title: 'No pudimos cargar el capítulo',
      body: 'El texto no llegó. Si reintentás y sigue igual, avisanos.',
      action: 'Reintentar',
    },
    saveStory: {
      title: 'No pudimos guardar los cambios',
      body: 'Tu texto sigue acá, no se perdió. Reintentá en unos segundos.',
      action: 'Reintentar',
    },
    upload: {
      title: 'No pudimos subir la portada',
      body: 'Revisá que sea JPG, PNG o WebP y que no pase los 2 MB.',
      action: 'Elegir otra imagen',
    },
    permission: {
      title: 'No tenés permiso para esto',
      body: 'Puede que hayas cerrado sesión en otra pestaña o que el contenido no sea tuyo.',
      action: 'Entrar de nuevo',
    },
    notFound: {
      title: 'Esta página no existe',
      body: 'El link puede estar mal escrito o el contenido pudo haberse borrado.',
      action: 'Ir al inicio',
    },
    crash: {
      title: 'Se nos rompió algo',
      body: 'La pantalla falló y no pudimos recuperarla sola. Recargar suele alcanzar.',
      action: 'Recargar',
    },
    rateLimited: {
      title: 'Estás yendo muy rápido',
      body: 'Alcanzaste el límite de publicaciones por ahora. Probá de nuevo en un rato.',
      action: 'Entendido',
    },
    offline: {
      title: 'Parece que estás sin conexión',
      body: 'No podemos traer contenido nuevo hasta que vuelva internet.',
      action: 'Reintentar',
    },
    /** No culpa a la conexión: el problema es del servidor y se resuelve solo. */
    indexMissing: {
      title: 'Esta consulta todavía no está lista',
      body: 'La base está terminando de preparar un índice para esta búsqueda. Suele tardar unos minutos y se arregla sola; no es tu conexión.',
      action: 'Reintentar',
    },
    /** Genérico honesto: nombra qué se estaba trayendo y no inventa la causa. */
    generic: {
      title: (subject: string) => `No pudimos traer ${subject}`,
      body: 'El pedido no llegó a completarse. Puede ser algo pasajero: reintentá en unos segundos.',
      action: 'Reintentar',
    },
  },

  a11y: {
    coverOf: (title: string) => `Portada de ${title}`,
    avatarOf: (name: string) => `Foto de ${name}`,
    typeBadge: (type: string) => `Tipo de contenido: ${type}`,
    ratingValue: (avg: number, count: number) => `${avg} de 5, según ${count} votos`,
    openMenu: 'Abrir menú',
    closeSheet: 'Cerrar panel',
    loadingMore: 'Cargando más resultados',
    currentPage: 'Página actual',
    pending: (n: number) => (n === 1 ? '1 cosa pendiente' : `${n} cosas pendientes`),
  },

  validation: {
    required: 'Este campo es obligatorio.',
    titleTooLong: 'El título no puede pasar los 120 caracteres.',
    synopsisTooLong: 'La sinopsis no puede pasar los 2000 caracteres.',
    fandomRequired: 'Elegí un fandom del catálogo.',
    warningsRequired: 'En fanfic las advertencias son obligatorias.',
    genresRequired: 'Elegí al menos un género.',
    genresTooMany: 'Máximo 3 géneros.',
    shipsTooMany: 'Máximo 5 ships.',
    emailInvalid: 'Ese correo no parece válido.',
    passwordTooShort: 'Mínimo 8 caracteres.',
  },

  /* Etiquetas de las pantallas legales. La prosa está en i18n/legal.ts. */
  legal: {
    updatedOn: 'Última actualización:',
    contactTitle: '¿Alguna duda sobre esto?',
    contactBody: 'Escribinos a',
    terms: 'Términos',
    privacy: 'Privacidad',
    takedown: 'Reclamos de autoría',
    footerRights: 'Lo que publicás sigue siendo tuyo.',
  },
} as const;

export type Copy = typeof es;
